import { Calendar, PreorderWithDates, Stock, StockWithDates } from '@app/components/various/Icons';
import { Tooltip } from '@app/components/various/Tooltip';
import { isRunning } from '@app/logic/deliveryWindows';
import { getDateFormatter, getHumanReadableTimePeriod } from '@app/utils/dates';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { DeliveryWindow } from '@typings';
import React from 'react';

import { DelwinMenuItemTooltipContent } from './DelwinMenuItemTooltipContent';

const format = 'MMM D';

const statusReason: Record<DeliveryWindow.StatusReasonKey, DeliveryWindow.StatusReason> = {
  isExpired: 'expired',
  isHidden: 'hidden',
};

interface Props {
  deliveryWindow: DeliveryWindow.DeliveryWindowsSearchItem;
}

const preventDefault = (evt: React.UIEvent) => {
  evt.stopPropagation();
  evt.preventDefault();
};

export const DelwinMenuItem = ({ deliveryWindow }: Props) => {
  const delwinStatusReason =
    deliveryWindow.isExpired ? statusReason.isExpired
    : deliveryWindow.isHidden ? statusReason.isHidden
    : null;

  const deliveryPeriod =
    isDefined(deliveryWindow.startDate) && isDefined(deliveryWindow.endDate) ?
      getHumanReadableTimePeriod(' - ')(format)([deliveryWindow.startDate, deliveryWindow.endDate])
    : null;

  const saleEndDate = isDefined(deliveryWindow.saleEndDate) ? getDateFormatter(format)(deliveryWindow.saleEndDate) : null;

  const Icon = React.useMemo(() => {
    if (isRunning(deliveryWindow)) {
      return deliveryWindow.stockType === 'stock' ? Stock : Calendar;
    }

    return deliveryWindow.stockType === 'stock' ? StockWithDates : PreorderWithDates;
  }, [deliveryWindow]);

  return (
    <span className="flex items-center gap-2 overflow-hidden">
      <Tooltip
        content={
          <DelwinMenuItemTooltipContent
            deliveryWindow={deliveryWindow}
            isRunning={!isDefined(deliveryWindow.endDate)}
            deliveryPeriod={deliveryPeriod}
            saleEndDate={saleEndDate}
          />
        }
      >
        <span className="ml-1 mr-0.5 inline-flex cursor-pointer items-center" role="presentation" onClick={preventDefault}>
          <Icon />
        </span>
      </Tooltip>
      <span className="overflow-hidden text-ellipsis body-2">{!isEmpty(deliveryWindow.name) ? deliveryWindow.name : '-'}</span>
      {isDefined(delwinStatusReason) && (
        <span className="overflow-hidden text-ellipsis text-neutral-60 body-2">({delwinStatusReason})</span>
      )}
    </span>
  );
};
